import BubbleCommon, {
  communitySteamingCommonTitleText,
  compareKeyMetricsCreatorTips,
  compareKeyMetricsEngagementRateTips,
  compareKeyMetricsEngagementTips,
  compareKeyMetricsImpressionsTips,
  compareKeyMetricsOfficeAccountEngagementTips,
  compareKeyMetricsOfficeAccountPublicationsTips,
  compareKeyMetricsOfficeAccountViewsTips,
  compareKeyMetricsPostCreatorTips,
  compareKeyMetricsPostImpressionsTips,
  compareKeyMetricsPostPublicationsTips,
  compareKeyMetricsPostsEngagementTips,
  compareKeyMetricsPostViewsTips,
  compareKeyMetricsPublicationsTips,
  compareKeyMetricsVideoCreatorTips,
  compareKeyMetricsVideoEngagementTips,
  compareKeyMetricsVideoImpressionsTips,
  compareKeyMetricsVideoPublicationsTips,
  compareKeyMetricsVideoViewsTips,
  compareKeyMetricsViewsTips,
  TipsMapType,
} from '@op/components/BubbleCommon';
import SvgIcon from '@/pages/home/components/SvgIcon';
import { chartAllColors, onlyShowInPre } from '@op/constants';
import { SymbolType } from '@op/constants/chart';
import { CommonColumnsType } from '@op/types/table';
import { getChartsGradientColor } from '@op/utils/chart';
import { reviewsFormat, specialSecimalFormatPercentage } from '@op/utils/value';
import React from 'react';
import BubbleGoPage from '../../component/BubbleGoPage';

export type CommunityOptionsType =
  | 'total'
  | 'videos'
  | 'posts'
  | 'streaming'
  | 'official_accounts';

export const getCommunityOptions: (item: {
  formatMessage: any;
  getOtherParams?: any;
  logId?: string;
  filter: any;
  tab?: CommunityOptionsType;
}) => {
  text: string | any;
  value: CommunityOptionsType;
}[] = ({ formatMessage, logId, getOtherParams, filter, tab }) => [
  {
    text: (
      <div className="dbo-inlineflex">
        <SvgIcon type="studio-all"></SvgIcon>
        <span>{formatMessage({ id: 'Total' })}</span>
      </div>
    ),
    value: 'total',
  },
  {
    text: (
      <BubbleGoPage
        title={formatMessage({ id: 'Go to Video page' })}
        logId={logId}
        pagePath={'/opinion/Community/Video'}
        filter={{ ...filter, ...getOtherParams('videos') }}
      >
        <div className="dbo-inlineflex">
          <SvgIcon type="studio-video"></SvgIcon>
          <span>{formatMessage({ id: 'Video' })}</span>
        </div>
      </BubbleGoPage>
    ),
    value: 'videos',
  },
  {
    text: (
      <BubbleGoPage
        pagePath={'/opinion/Feeds/Feeds'}
        filter={{ ...filter, ...getOtherParams('posts') }}
        title={formatMessage({ id: 'Go to Feeds page' })}
        logId={logId}
      >
        <div className="dbo-inlineflex">
          <SvgIcon type="studio-posts"></SvgIcon>
          <span>{formatMessage({ id: 'Posts-2' })}</span>
        </div>
      </BubbleGoPage>
    ),
    value: 'posts',
  },
  {
    text: (
      <div className="dbo-inlineflex">
        <BubbleGoPage
          pagePath={'/opinion/Community/Streaming'}
          filter={{ ...filter, ...getOtherParams('streaming') }}
          title={formatMessage({ id: 'Go to Streaming page' })}
          logId={logId}
        >
          <SvgIcon type="studio-streaming"></SvgIcon>
          <span>{formatMessage({ id: 'Streaming' })}</span>
        </BubbleGoPage>
        <BubbleCommon
          placement="top"
          selected={tab === 'streaming'}
          tipsMap={communitySteamingCommonTitleText}
        ></BubbleCommon>
      </div>
    ),
    value: 'streaming',
  },
  ...onlyShowInPre({
    text: (
      <BubbleGoPage
        pagePath={'/opinion/Accounts/ByAccount'}
        filter={{ ...filter, ...getOtherParams('official_accounts') }}
        title={formatMessage({ id: 'Go to Official Account' })}
        logId={logId}
      >
        <div className="dbo-inlineflex">
          <SvgIcon type="offical-accounts"></SvgIcon>
          <span>{formatMessage({ id: 'Official Accounts' })}</span>
        </div>
      </BubbleGoPage>
    ),
    value: 'official_accounts',
  }),
];

export type KeyMetricsType =
  | 'impressions'
  | 'publications'
  | 'creators'
  | 'views'
  | 'engagement'
  | 'engagement_rate'
  | 'hoursWatched'
  | 'sessions'
  | 'streamers'
  | 'peakCCV'
  | 'avgCCV';

export const streamingKeyMetricMap: { [key in KeyMetricsType]?: string } = {
  sessions: 'publications',
  streamers: 'creators',
};

export type KeyMetricsItemType = {
  text: string;
  key: KeyMetricsType;
  tips?: TipsMapType;
  format?: (value: number) => any;
};

const commonKeyMetrics: (
  tipsMap: {
    viewsTips?: TipsMapType;
    engagementTips?: TipsMapType;
    creatorsTips?: TipsMapType;
    impressionsTips?: TipsMapType;
    publicationsTips?: TipsMapType;
  },
  headerTextMap: { views?: string },
) => Array<KeyMetricsItemType> = (
  {
    viewsTips = compareKeyMetricsViewsTips,
    engagementTips = compareKeyMetricsEngagementTips,
    creatorsTips = compareKeyMetricsCreatorTips,
    impressionsTips = compareKeyMetricsImpressionsTips,
    publicationsTips = compareKeyMetricsPublicationsTips,
  },
  { views = 'Views' },
) => [
      {
        text: 'Potential Impressions',
        key: 'impressions',
        tips: impressionsTips,
      },
      {
        text: 'Publications',
        key: 'publications',
        tips: publicationsTips,
      },
      {
        text: 'Number of Creators',
        key: 'creators',
        tips: creatorsTips,
      },
      {
        text: views,
        key: 'views',
        tips: viewsTips,
      },
      {
        text: 'Engagement',
        key: 'engagement',
        tips: engagementTips,
      },
      {
        text: 'Engagement Rate',
        key: 'engagement_rate',
        tips: compareKeyMetricsEngagementRateTips,
        format: (value: number) => specialSecimalFormatPercentage(value),
      },
    ];

export const keyMetricsOptionsMap: {
  [key in CommunityOptionsType]: Array<KeyMetricsItemType>;
} = {
  total: [
    ...commonKeyMetrics({}, {}),
    // {
    //   text: 'Hours Watched',
    //   key: 'hoursWatched',
    // },
  ],
  videos: commonKeyMetrics(
    {
      viewsTips: compareKeyMetricsVideoViewsTips,
      engagementTips: compareKeyMetricsVideoEngagementTips,
      creatorsTips: compareKeyMetricsVideoCreatorTips,
      impressionsTips: compareKeyMetricsVideoImpressionsTips,
      publicationsTips: compareKeyMetricsVideoPublicationsTips,
    },
    { views: 'Views-1' },
  ),
  posts: commonKeyMetrics(
    {
      viewsTips: compareKeyMetricsPostViewsTips,
      engagementTips: compareKeyMetricsPostsEngagementTips,
      creatorsTips: compareKeyMetricsPostCreatorTips,
      impressionsTips: compareKeyMetricsPostImpressionsTips,
      publicationsTips: compareKeyMetricsPostPublicationsTips,
    },
    {},
  ),
  streaming: [
    // {
    //   text: 'Sessions',
    //   key: 'sessions',
    // },
    // {
    //   text: 'Streamers',
    //   key: 'streamers',
    // },
    {
      text: 'Hours Watched',
      key: 'hoursWatched',
    },
    {
      text: 'Peak CCV',
      key: 'peakCCV',
    },
    {
      text: 'Avg CCV',
      key: 'avgCCV',
    },
  ],
  official_accounts: [
    {
      text: 'Potential Impressions',
      key: 'impressions',
      tips: compareKeyMetricsImpressionsTips,
    },
    {
      text: 'Publications',
      key: 'publications',
      tips: compareKeyMetricsOfficeAccountPublicationsTips,
    },
    {
      text: 'Views',
      key: 'views',
      tips: compareKeyMetricsOfficeAccountViewsTips,
    },
    {
      text: 'Engagement',
      key: 'engagement',
      tips: compareKeyMetricsOfficeAccountEngagementTips,
    },
    {
      text: 'Engagement Rate',
      key: 'engagement_rate',
      tips: compareKeyMetricsEngagementRateTips,
      format: (value: number) => specialSecimalFormatPercentage(value),
    },
  ],
};

export type ByKeyMetricsType =
  | 'by_channel'
  | 'by_language'
  | 'by_media_type'
  | 'by_follower_range'
  | 'by_engagement_type'
  | 'total';

type KeyMetricsSplitOptionsItem = {
  text: string;
  value: ByKeyMetricsType;
  tabs?: Array<CommunityOptionsType>;
};

const splitTotal: KeyMetricsSplitOptionsItem = {
  text: 'Total',
  value: 'total',
};
const splitChannel: KeyMetricsSplitOptionsItem = {
  text: 'By Channel',
  value: 'by_channel',
};
const splitLanguage: KeyMetricsSplitOptionsItem = {
  text: 'By Language',
  value: 'by_language',
};

const splitMediaType: KeyMetricsSplitOptionsItem = {
  text: 'By Media Type',
  value: 'by_media_type',
};

export const keyMetricsSplitOptions: {
  [key in KeyMetricsType]: Array<KeyMetricsSplitOptionsItem>;
} = {
  impressions: [splitChannel, splitMediaType, splitLanguage, splitTotal],
  publications: [splitChannel, splitMediaType, splitLanguage, splitTotal],
  creators: [
    splitChannel,
    // splitLanguage,
    {
      text: 'By Follower Range',
      value: 'by_follower_range',
    },
    splitTotal,
  ],
  views: [splitChannel, splitMediaType, splitLanguage, splitTotal],
  engagement: [
    {
      text: 'By Engagement Type',
      value: 'by_engagement_type',
    },
    splitChannel,
    splitMediaType,
    splitLanguage,
    splitTotal,
  ],
  engagement_rate: [splitChannel, splitMediaType, splitLanguage, splitTotal],
  hoursWatched: [splitChannel],
  sessions: [splitChannel],
  streamers: [
    splitChannel,
    {
      text: 'By Follower Range',
      value: 'by_follower_range',
    },
  ],
  peakCCV: [splitChannel],
  avgCCV: [splitChannel],
};

export type TableColumnsFuncType = (
  list: Array<{ header: string; key: string; format?: (value: any) => string }>,
) => Array<CommonColumnsType>;

const channelLanguageTable: TableColumnsFuncType = (list) => {
  return [
    ...list.map(({ header, key, format = reviewsFormat, ...item }) => {
      return {
        header,
        key,
        format,
        ...item,
      };
    }),
  ];
};

const byTypeSplitOptions: {
  [key in ByKeyMetricsType]:
  | Array<
    CommonColumnsType & {
      tabs?: Array<CommunityOptionsType>; // 只有在这个tab下才存在
      keyMetric?: KeyMetricsType; // 只有在该指标下存在
    }
  >
  | TableColumnsFuncType;
} = {
  total: channelLanguageTable,
  by_channel: channelLanguageTable,
  by_language: channelLanguageTable,
  by_media_type: [
    { key: 'video_impressions', header: 'Video', keyMetric: 'impressions' },
    { key: 'posts_impressions', header: 'Posts-2', keyMetric: 'impressions' },
    { key: 'video_views', header: 'Video', keyMetric: 'views' },
    { key: 'posts_views', header: 'Posts-2', keyMetric: 'views' },
    {
      key: 'video_engagement',
      header: 'Video',
      tabs: ['total', 'official_accounts'],
      keyMetric: 'engagement',
    },
    {
      key: 'posts_engagement',
      header: 'Posts-2',
      tabs: ['total', 'official_accounts'],
      keyMetric: 'engagement',
    },
    {
      key: 'video_comments_engagement',
      header: 'Comments on Videos',
      tabs: ['total', 'official_accounts'],
      keyMetric: 'engagement',
    },
    {
      key: 'posts_comments_engagement',
      header: 'Comments on Posts',
      tabs: ['total', 'official_accounts'],
      keyMetric: 'engagement',
    },
    {
      key: 'video_engagement_rate',
      header: 'Video',
      // tabs: ['total', 'official_accounts'],
      keyMetric: 'engagement_rate',
    },
    {
      key: 'posts_engagement_rate',
      header: 'Posts-2',
      // tabs: ['total', 'official_accounts'],
      keyMetric: 'engagement_rate',
    },
    {
      key: 'video_comments_engagement_rate',
      header: 'Comments on Videos',
      // tabs: ['total', 'official_accounts'],
      keyMetric: 'engagement_rate',
    },
    {
      key: 'posts_comments_engagement_rate',
      header: 'Comments on Posts',
      // tabs: ['total', 'official_accounts'],
      keyMetric: 'engagement_rate',
    },
    {
      key: 'video_publications',
      header: 'Video',
      tabs: ['total', 'videos', 'official_accounts'],
      keyMetric: 'publications',
    },
    {
      key: 'posts_publications',
      header: 'Posts-2',
      tabs: ['total', 'posts', 'official_accounts'],
      keyMetric: 'publications',
    },
    {
      key: 'streaming',
      header: 'Streaming',
      tabs: ['total'],
      keyMetric: 'publications',
    },
  ],
  by_follower_range: [
    { key: 'unknown', header: 'Unknown' },
    { key: 'common', header: 'Common Publisher (0-1K)' },
    { key: 'nano', header: 'Nano (1-10K)' },
    { key: 'micro', header: 'Micro (10K-50K)' },
    { key: 'mid-tier', header: 'Mid-Tier (50K-500K)' },
    { key: 'macro', header: 'Macro (500K-1M)' },
    { key: 'mega', header: 'Mega (1M+)' },
  ],
  by_engagement_type: [
    { key: 'likes', header: 'Likes' },
    { key: 'tweets_comments', header: 'Comments' },
    { key: 'shares', header: 'Shares' },
  ],
};

/** Chart series key/header → feeds sort_item for detail page */
export const ENGAGEMENT_TYPE_SORT_MAP: Record<string, string> = {
  likes: 'tweets_like',
  tweets_comments: 'tweets_reply',
  shares: 'share',
  Likes: 'tweets_like',
  Comments: 'tweets_reply',
  Shares: 'share',
};

export function getEngagementSortItem(value: string): string | undefined {
  return ENGAGEMENT_TYPE_SORT_MAP[value];
}

/** Tooltip sort label: Like | Comment | Share */
export const ENGAGEMENT_TYPE_TIP_SORT_LABEL: Record<string, string> = {
  likes: 'Like',
  tweets_comments: 'Comment',
  shares: 'Share',
  Likes: 'Like',
  Comments: 'Comment',
  Shares: 'Share',
};

export function getEngagementTipSortLabel(value: string): string | undefined {
  return ENGAGEMENT_TYPE_TIP_SORT_LABEL[value];
}

/** Chart media type key/header → detail review_types */
export const MEDIA_TYPE_REVIEW_TYPES_MAP: Record<string, string[]> = {
  // Post
  'Posts-2': ['posts_text'],
  posts_impressions: ['posts_text'],
  posts_views: ['posts_text'],
  posts_engagement: ['posts_text'],
  posts_engagement_rate: ['posts_text'],
  posts_publications: ['posts_text'],
  // Video
  Video: ['posts_video', 'stream_titles'],
  video_impressions: ['posts_video', 'stream_titles'],
  video_views: ['posts_video', 'stream_titles'],
  video_engagement: ['posts_video', 'stream_titles'],
  video_engagement_rate: ['posts_video', 'stream_titles'],
  video_publications: ['posts_video', 'stream_titles'],
  // Comments on Posts
  'Comments on Posts': ['sub_text'],
  posts_comments_engagement: ['sub_text'],
  posts_comments_engagement_rate: ['sub_text'],
  // Comments on Videos
  'Comments on Videos': [
    'sub_video',
    'comments_on_streams',
    'live_chats_on_streams',
  ],
  video_comments_engagement: [
    'sub_video',
    'comments_on_streams',
    'live_chats_on_streams',
  ],
  video_comments_engagement_rate: [
    'sub_video',
    'comments_on_streams',
    'live_chats_on_streams',
  ],
  // Streaming (publications only)
  Streaming: ['stream_titles'],
  streaming: ['stream_titles'],
};

const LIVE_REVIEW_TYPES = [
  'stream_titles',
  'comments_on_streams',
  'live_chats_on_streams',
];

function hasMonitorLiveAccount(monitorByAccountCategory?: {
  monitory?: Record<string, any[]>;
}): boolean {
  const obj = monitorByAccountCategory?.monitory || {};
  return Object.keys(obj).some((key) =>
    obj[key]?.some((item: any) => item.channel?.includes('live')),
  );
}

export function getReviewTypesFromMedia(
  value: string,
  monitorByAccountCategory?: { monitory?: Record<string, any[]> },
): string[] | undefined {
  if (!value) return undefined;
  const reviewTypes = MEDIA_TYPE_REVIEW_TYPES_MAP[value];
  if (!reviewTypes?.length) return undefined;

  if (hasMonitorLiveAccount(monitorByAccountCategory)) {
    return reviewTypes;
  }

  return reviewTypes.filter((type) => !LIVE_REVIEW_TYPES.includes(type));
}

export function getCurrentColumns({
  tab,
  keyMetric,
  byTypeKeyMetric,
}: {
  tab: CommunityOptionsType;
  byTypeKeyMetric: ByKeyMetricsType;
  keyMetric: KeyMetricsType;
}) {
  const list = byTypeSplitOptions[byTypeKeyMetric];
  if (typeof list === 'function') return list;

  return list.filter(({ tabs, keyMetric: current }) => {
    return (
      (!tabs || tabs?.includes(tab)) && (!current || current === keyMetric)
    );
  });
}

export function getChartsOptions({
  data,
  chartType,
  keys = [],
  chartRealType,
}: {
  data: any;
  chartType: SymbolType;
  keys: Array<{ key: string; header: string; color?: string }>;
  chartRealType: 'bar' | 'line';
}) {
  function getDataInfo(key: string) {
    return data[key]?.map((val: number) => val || null) || [];
  }

  const series: Array<any> = [],
    legend: { [key: string]: any } = { data: [] };

  keys?.forEach(({ key, header, color }, index) => {
    const styleColor = color || chartAllColors[index];
    const item: any = {
      name: header,
      type: chartRealType,
      data: getDataInfo(key),
      itemStyle: { color: styleColor },
    };
    if (['bar', 'stack_line'].includes(chartType)) {
      item.stack = 'Total';
      if (chartType === 'stack_line') {
        item.areaStyle = getChartsGradientColor(styleColor, 0, 0.4);
      }
    }
    series.push(item);

    legend.data.push({
      name: header,
      itemStyle: { color: styleColor },
    });
  });

  return { series, legend };
}


export const defaultFilterReviewTypes: { [key in CommunityOptionsType]: string[] } = {
  total: ["posts_text", "posts_video"],
  videos: ["posts_video"],
  posts: ["posts_text"],
  streaming: [],
  official_accounts: [],
};